import React, { useState, useEffect, useCallback } from 'react';
import { Header } from '@appkit4/react-components/header';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import AvatarDropdown from './AvatarDropdown';
import { AppRoutes } from '../../Constants';
import useLocale from '../../locale/useLocale';
import useToast from '../../helpers/useToast';
import './Navigation.scss';
import Tabs from '../shared/tabs/Tabs';
import { useGetV1IdentityUserInfo } from '../../api/generated/endpoint';
import Roles from '../../auth/roles';
import AvailableExports from './AvailableExports';
import {
    GetProcessExportHistoryResponse,
    ProcessExportStatusEnum,
} from '../../api/generated/models';
import { useExportPolling } from '../../helpers/useExportPolling';

const Navigation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [headerTabs, setHeaderTabs] = useState([
        {
            label: 'Home',
            value: 'home',
            //icon: 'icon-dashboard-outline',
        },
        {
            label: 'My engagements',
            value: 'engagements',
            //icon: 'icon-document-file-outline',
        },
    ]);
    const auth = useAuth();
    const showToast = useToast();
    const { l } = useLocale();
    const [activeTab, setActiveTab] = useState(0);

    const { data: userData } = useGetV1IdentityUserInfo({
        query: {
            staleTime: Infinity,
        },
    });

    const [dropDownData, setDropDownData] = useState([] as any);

    const handleExportComplete = useCallback((fileName: string) => {
        showToast(
            'Export Completed',
            `Export '${fileName}' has been completed.`,
            'success'
        );
    }, []);

    // const { data: processExportData } = useGetV1ProcessExportHistory();

    const { data: processExportData } = useExportPolling({
        interval: 5000, // 5 seconds
        onExportComplete: handleExportComplete,
    });

    useEffect(() => {
        const data = processExportData?.map(
            (exportFile: GetProcessExportHistoryResponse) => {
                return {
                    label: `${exportFile.exportFileName} - ${exportFile.statusName}`,
                    value: exportFile.transactionId,
                    status: exportFile.status,
                    disabled:
                        exportFile.status == ProcessExportStatusEnum.NUMBER_1,
                    iconName:
                        exportFile.status == ProcessExportStatusEnum.NUMBER_1
                            ? 'wait-outline'
                            : 'download-outline',
                };
            }
        );
        setDropDownData(data);
    }, [processExportData]);

    useEffect(() => {
        if (userData?.roleId && userData.roleId >= Roles.AdminId) {
            setHeaderTabs([
                ...headerTabs,
                {
                    label: 'Process settings',
                    value: 'settings',
                },
            ]);
        }
    }, [userData]);

    useEffect(() => {
        if (location.pathname === AppRoutes.HomePage) {
            setActiveTab(0);
        } else if (location.pathname.startsWith(AppRoutes.FolderRoot)) {
            setActiveTab(1);
        } else if (
            (location.pathname.startsWith(AppRoutes.ExtractionSettings) ||
                location.pathname.startsWith(AppRoutes.FillPDFSettings) ||
                location.pathname.startsWith(AppRoutes.FillOnlineSettings)) &&
            headerTabs.length > 2
        ) {
            setActiveTab(2);
        }
    }, [location, headerTabs]);

    const handleTabChange = (index: number, value: string) => {
        setActiveTab(index);
        switch (value) {
            case 'home':
                navigate(AppRoutes.HomePage);
                break;
            case 'engagements':
                navigate(AppRoutes.FolderRoot);
                break;
            case 'settings':
                navigate(AppRoutes.ExtractionSettings);
                break;
        }
    };

    const titleTemplate = () => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <Link
                to={
                    auth.isAuthenticated
                        ? AppRoutes.HomePage
                        : AppRoutes.LandingPage
                }
                style={{
                    fontWeight: 'normal',
                    textDecorationLine: 'none',
                    marginRight: '40px',
                }}
            >
                {l('_applicationName')}
            </Link>
            {/* <Tabs
                tabs={headerTabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                className="header-tabs"
            /> */}
        </div>
    );

    const contentTemplate = () => (
        <div
            style={{ display: 'flex', alignItems: 'center' }}
            className="header-tabs"
        >
            <Tabs
                tabs={headerTabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
            />
        </div>
    );

    const userTemplate = () =>
        auth.isAuthenticated ? <AvatarDropdown /> : null;

    const handleLogoNavigation = () =>
        auth.isAuthenticated
            ? navigate(AppRoutes.HomePage)
            : navigate(AppRoutes.LandingPage);

    return (
        <div className="navigation-wrapper">
            {dropDownData && (
                <Header
                    role="navigation"
                    hasLogo
                    type="solid"
                    titleTemplate={titleTemplate}
                    contentTemplate={contentTemplate}
                    userTemplate={userTemplate}
                    optionsTemplate={() => {
                        return (
                            <>
                                <AvailableExports dropDownData={dropDownData} />
                            </>
                        );
                    }}
                    onClickLogo={handleLogoNavigation}
                    className="position-sticky"
                    style={{ top: '0' }}
                />
            )}
        </div>
    );
};

export default Navigation;
